import * as React from "react";

import "./toolbar.css";

/**
 * Toolbar, ToolbarContent and Tooltray, replacing `@salt-ds/core`'s behind `data-filters.tsx`.
 *
 * ── WHAT `role="toolbar"` PROMISES, AND WHEN SALT DOES NOT KEEP IT ────────────────────────
 *
 * `role="toolbar"` tells assistive technology that the controls inside form ONE tab stop and
 * that arrow keys move between them. Salt honours that only when its children are `Tooltray`
 * elements. Anything else — a `Text`, a `Button`, a filter control placed directly inside a
 * `ToolbarContent` — makes `normalizeToolbarChildren` return `mode: "invalid"`, and the toolbar
 * falls back to rendering its children verbatim with no roving tabindex at all.
 *
 * MEASURED in Chrome, not inferred. The application's own filter bar, rendered exactly as
 * `data-filters.tsx` writes it:
 *
 *   mode=invalid   classes: saltToolbar saltToolbar-fallback …   tabindexes: []
 *
 * versus the same Toolbar given a Tooltray child:
 *
 *   mode=flat      classes: saltToolbar saltToolbar-layout …     tabindexes: [-1]
 *
 * So the filter bar advertises keyboard navigation it does not provide. `Tooltray` appears
 * nowhere in this application, so this is not a near miss — it is every Toolbar here. The
 * defect is in the CALL SITE, not in this port, and it is recorded rather than quietly fixed:
 * restructuring the filter bar changes app behaviour and belongs in its own change.
 *
 * ── THE OVERFLOW SYSTEM IS DELIBERATELY NOT PORTED ────────────────────────────────────────
 *
 * Roughly 1,600 of the vendor's 3,000 lines implement collapsing overflowing items into a
 * floating menu, driven by off-screen measurement clones and ResizeObserver. It is reachable
 * only from the Tooltray path, which nothing here uses, and it cannot be exercised in jsdom at
 * all — there is no layout engine, so every measurement returns zero.
 *
 * Porting it would mean shipping ~1,600 lines whose correctness could not be verified, to serve
 * no call site. It is left out ON PURPOSE, and the omission is LOUD rather than silent: the
 * Tooltray path warns in development instead of rendering a toolbar that quietly never
 * collapses. Silent degradation is the exact failure this component already has.
 */
export type ToolbarAppearance = "bordered" | "transparent";
export type ToolbarVariant = "primary" | "secondary" | "tertiary";
export type ToolbarPosition = "start" | "center" | "end";

export interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  appearance?: ToolbarAppearance;
  variant?: ToolbarVariant;
}

export interface ToolbarContentProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: ToolbarPosition;
}

export interface TooltrayProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "end";
  overflowGroup?: string;
  overflowMode?: "shared" | "named" | "none";
}

/** The attribute Salt's keyboard navigation uses to find the root of a toolbar scope. */
const TOOLBAR_SCOPE_ROOT_ATTR = "data-salt-toolbar-scope-root";

export const ToolbarContent = React.forwardRef<HTMLDivElement, ToolbarContentProps>(
  function ToolbarContent({ children, className, position, ...rest }, ref) {
    return (
      <div
        className={["saltToolbarContent", className].filter(Boolean).join(" ")}
        {...rest}
        data-position={position}
        ref={ref}
      >
        {children}
      </div>
    );
  },
);

export const Tooltray = React.forwardRef<HTMLDivElement, TooltrayProps>(function Tooltray(
  { align, children, className, overflowGroup, overflowMode, ...rest },
  ref,
) {
  return (
    <div
      className={["saltTooltray", className].filter(Boolean).join(" ")}
      data-align={align}
      data-overflow-group={overflowGroup}
      data-overflow-mode={overflowMode}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

/**
 * Classify children exactly as the vendor does.
 *
 * `"explicit"` — every child is a ToolbarContent, and every one of THEIR children is a Tooltray
 * or a Divider. `"flat"` — every child is a Tooltray or a Divider. Anything else is
 * `"invalid"`, and that is not an error state: it is the path almost every real call site takes.
 */
function classifyChildren(children: React.ReactNode): "explicit" | "flat" | "invalid" {
  const flattened: React.ReactElement[] = [];
  const flatten = (nodes: React.ReactNode) => {
    React.Children.forEach(nodes, (child) => {
      if (child == null || typeof child === "boolean") return;
      if (React.isValidElement(child) && child.type === React.Fragment) {
        flatten((child.props as { children?: React.ReactNode }).children);
        return;
      }
      if (React.isValidElement(child)) flattened.push(child);
    });
  };
  flatten(children);
  if (!flattened.length) return "invalid";

  const isContent = (c: React.ReactElement) => c.type === ToolbarContent;
  const isTray = (c: React.ReactElement) => c.type === Tooltray;

  if (flattened.every(isContent)) {
    // A ToolbarContent whose own children are not all Tooltrays drags the WHOLE toolbar to
    // invalid. That single rule is why the application's filter bar has no keyboard navigation.
    const everyContentHoldsTrays = flattened.every((content) => {
      const inner: React.ReactElement[] = [];
      React.Children.forEach(
        (content.props as { children?: React.ReactNode }).children,
        (child) => {
          if (child == null || typeof child === "boolean") return;
          if (React.isValidElement(child)) inner.push(child);
        },
      );
      return inner.length > 0 && inner.every(isTray);
    });
    return everyContentHoldsTrays ? "explicit" : "invalid";
  }
  if (flattened.every(isTray)) return "flat";
  return "invalid";
}

export const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(function Toolbar(
  { appearance = "bordered", children, className, variant = "primary", ...rest },
  ref,
) {
  const mode = classifyChildren(children);

  React.useEffect(() => {
    if (mode !== "invalid" && import.meta.env.DEV) {
      // Loud on purpose. A toolbar in layout mode is asking for the overflow behaviour, and
      // this port does not implement it. Better a console warning than a bar that silently
      // never collapses and looks broken only at narrow widths.
      console.warn(
        "[design-system] Toolbar: overflow collapsing is not implemented in this port. " +
          "Items will render without collapsing into a menu. See src/design-system/navigation/Toolbar.tsx.",
      );
    }
  }, [mode]);

  const classes = ["saltToolbar"];
  classes.push(mode === "invalid" ? "saltToolbar-fallback" : "saltToolbar-layout");
  classes.push(`saltToolbar-${variant}`, `saltToolbar-${appearance}`);
  if (className) classes.push(className);

  return (
    <div
      aria-orientation="horizontal"
      className={classes.join(" ")}
      {...rest}
      {...{ [TOOLBAR_SCOPE_ROOT_ATTR]: "main" }}
      data-mode={mode}
      ref={ref}
      role="toolbar"
    >
      {children}
    </div>
  );
});
